import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import LdJson from '../../data/schema';
import { SiteNavigation } from '../siteNavigation/SiteNavigation';
import Footer from '../footer/Footer';
import Button from '../button/Button';
import OnboardingContext from '../../context/OnboardingContext';
import ThemeContext from '../../context/ThemeContext';
import { CookieModalProvider } from '@starlingbank/cookie-modal';
import { ABScriptLoader } from '../abScriptLoader/AbScriptLoader';
import { adjustAnchorScrollOnLoad, sourceRequiresRecaptcha } from '../../utils';
import * as classes from './Layout.module.scss';
import '../../styles/main.scss';

const pageSchema = (schemaEntities) => {
  Object.keys(schemaEntities).forEach(
    (k) => !schemaEntities[k] && schemaEntities[k] !== undefined && delete schemaEntities[k]
  );
  return [schemaEntities];
};

const Layout = ({ children, bodyClass, Meta, fields, schemaEntities = [], webflowRoute = false }) => {
  const { navWide } = useContext(ThemeContext);
  const schema = [
    ...(Meta && Meta.SchemaEntities
      ? Meta.SchemaEntities.map((entity) => pageSchema(entity))
      : schemaEntities
      ? schemaEntities
      : [])
  ];
  const currentPath = fields ? fields.slug : '';

  const requiresRecaptcha = fields?.source ? sourceRequiresRecaptcha(JSON.parse(fields.source)) : false;

  let grecaptchaKey = '';
  if (typeof window !== 'undefined') {
    grecaptchaKey = window.starlingbank.getConfig('grecaptchaKey');
  }

  useEffect(() => {
    const scrollOnLoad = bodyClass?.includes('scroll-on-load');
    const selector = window.location.hash;
    if (!scrollOnLoad || !selector) return;
    const element = document.querySelector(selector);
    if (element) adjustAnchorScrollOnLoad(element, navWide);
  }, [bodyClass, navWide]);

  const pageHasWebflowForm = fields?.source
    ? JSON.parse(fields.source).some((components) => Object.keys(components).includes('OnboardingForm'))
    : false;

  useEffect(() => {
    const webflowUrlPattern = new RegExp('^/apply/?$', 'g');
    if (!webflowUrlPattern.test(window.location.pathname) && !pageHasWebflowForm) {
      window.sessionStorage.setItem('onboardingPrevPage', window.location.pathname);
    }
  }, [pageHasWebflowForm]);

  return (
    <CookieModalProvider>
      <OnboardingContext.Provider
        value={{
          route: webflowRoute,
          hideCta: Meta?.NavbarCta?.HideCta ?? false
        }}
      >
        <Helmet bodyAttributes={{ class: `gatsby ${bodyClass || ''}` }}>
          {Meta && Meta.NoIndex && <meta name="robots" content="noindex, nofollow" />}
          {schema.map((schemaTag, i) => (
            <script key={`schema-${i}`} type="application/ld+json">
              {LdJson({ ...Meta, pathName: currentPath }, schemaTag[0])}
            </script>
          ))}
          {(Meta?.GreCaptcha || requiresRecaptcha) && grecaptchaKey && (
            <script key="0-googleCaptcha" src={`https://www.google.com/recaptcha/api.js?render=${grecaptchaKey}`} />
          )}
        </Helmet>

        <ABScriptLoader />

        <SiteNavigation forCampaign={Meta?.forCampaign || false} />

        {children}

        <Footer currentPath={currentPath} />

        {currentPath !== '/help/' && (
          <Button id="help-button" className={classes.helpButton} asLink href="/help/">
            <svg viewBox="0 0 8 13" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Help button">
              <path
                d="M4.892 8.8v-.24c0-.267.053-.499.16-.696.107-.197.24-.365.4-.504.16-.139.39-.315.688-.528.384-.277.699-.53.944-.76.245-.23.456-.53.632-.904.176-.373.264-.821.264-1.344 0-.533-.13-1.053-.392-1.56-.261-.507-.68-.93-1.256-1.272C5.756.651 5.02.48 4.124.48c-.79 0-1.47.133-2.04.4-.57.267-1.021.63-1.352 1.088-.33.459-.56.981-.688 1.568l1.824.48c.128-.533.36-.965.696-1.296.336-.33.835-.496 1.496-.496.427 0 .787.075 1.08.224.293.15.512.352.656.608a1.7 1.7 0 01.216.848c0 .299-.056.55-.168.752-.112.203-.25.376-.416.52a6.179 6.179 0 01-.696.504 8.093 8.093 0 00-.912.68c-.235.208-.435.49-.6.848-.165.357-.248.808-.248 1.352v.24h1.92zm-.928 3.328c.32 0 .595-.115.824-.344.23-.23.344-.52.344-.872 0-.31-.115-.579-.344-.808a1.124 1.124 0 00-.824-.344c-.341 0-.63.115-.864.344-.235.23-.352.499-.352.808a1.189 1.189 0 001.216 1.216z"
                fill="currentColor"
                fillRule="nonzero"
              />
            </svg>

            <span>Help</span>
          </Button>
        )}
      </OnboardingContext.Provider>
    </CookieModalProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  webflowRoute: PropTypes.oneOfType([PropTypes.oneOf([false]), PropTypes.string])
};

export default Layout;
